
 /*
  *****************************************************************************
  * WARNING: This file is automatically generated.
  * Do not edit this file manually.
  *****************************************************************************
  *
  * The source file is located at:
  *   /codebuild/output/src2723206075/src/vl-app/pages/patients/documents.tsx
  */

import { createFileRoute } from '@tanstack/react-router';
import React, { useEffect, useState } from 'react';
import { useRouter } from '@hooks/useRouter';
import { Row, Col, Button } from 'antd';
import { useInitialisedStore } from '@hooks/useInitialisedStore';
import useUser from '@vl-core/hooks/useUser';
import CaseMenu from '@components/Filter/CaseMenu';
import UploadDocumentModal from 'vl-common/src/components/UploadDocument/UploadDocumentModal';
import PatientMediaPermissionsWarning from '@components/MediaPermissions/PatientMediaPermissionsWarning';
import { PatientPageHeading, PatientPageSubHeading } from '@components/Layouts/PageHead/PatientPageHeading';
import SelfDischarge from '@components/Patients/SelfDischarge/SelfDischarge';
import { Case } from 'vl-common/src/schemas/shared/case';
import { useTranslation } from 'react-i18next';
import TypographyExpandEllipsis from 'vl-common/src/components/UI/ExpandEllipsis';
import { GetDocmanConsentForm, usePatientsGPAcceptsDocman } from '@components/Patients/Docman/MaybeGetDocmanConsent';
import FlexibleQuestions, { Survey } from '@components/SurveyBox/FlexibleQuestions';
import useRuntimeConfig from '@vl-core/useRuntimeConfig';
import { useQueryClient } from '@tanstack/react-query';
import Header from '@components/Layouts/Header/Header';
import LetterDocuments from '@components/Documents/LetterDocuments';

const Documents = () => {
  const user = useUser();
  const patientsGpAcceptsDocman = usePatientsGPAcceptsDocman();
  const initialised = useInitialisedStore();
  const { DOCMAN } = useRuntimeConfig();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user?.restricted) {
      queryClient.invalidateQueries({
        queryKey: ['getGPDetails', user?.user_guid]
      });
    }
  }, [queryClient]);

  const {
    query: { appt: apptId, caseId, caseCondition }
  } = useRouter();

  const encryptedApptId = apptId ? Buffer.from(`${apptId}`, 'base64').toString() : undefined;
  const encryptedCaseId = caseId ? Buffer.from(`${caseId}`, 'base64').toString() : null;
  const encryptedCaseCondition = caseCondition ? Buffer.from(`${caseCondition}`, 'base64').toString() : undefined;
  const { t } = useTranslation('common');

  const [selectedCaseId, setSelectedCaseId] = useState(
    encryptedCaseId || sessionStorage.getItem('case-filter-selected') || null
  );
  const [selectedCaseData, setSelectedCaseData] = useState<Partial<Case>>(() =>
    encryptedCaseId && encryptedCaseCondition
      ? {
          case_id: parseInt(encryptedCaseId),
          condition: encryptedCaseCondition
        }
      : {}
  );
  const [newCaseCreated, setNewCaseCreated] = useState('');
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [documentsRefresh, setDocumentsRefresh] = useState(0);
  const [docmanConsentModalVisible, setDocmanConsentModalVisible] = useState(false);
  const [surveyInformation, setSurveyInformation] = useState<Survey[]>();

  const isCaseDischarged = selectedCaseData?.case_status === 'DISCHARGE';
  const isConsentManageAllowed = patientsGpAcceptsDocman && DOCMAN && !user?.nhs_client && !isCaseDischarged;
  const surveyWithCaseIdDetails = surveyInformation?.find((survey) => {
    return survey.case_id !== null && survey.shortname !== 'General Health';
  });

  if (!initialised) {
    return <p>{t('Initialising ...')}</p>;
  }

  if (encryptedApptId && !Number(encryptedApptId)) return null;

  return (
    <div className="page-container">
      <Header pageType="patients" pageTitle="Documents" />
      <section className="section-layout patient" id="page-content">
        <main className="container">
          <PatientMediaPermissionsWarning />
          <PatientPageHeading>{t('Documents')}</PatientPageHeading>
          <TypographyExpandEllipsis maxRows={2} paragraphText={t('DocumentsPageDescription')} />
          {encryptedApptId ? (
            <LetterDocuments appId={Number(encryptedApptId)} />
          ) : (
            <>
              <Row gutter={[8, 8]} align={{ lg: 'middle' }}>
                <Col flex="auto">
                  <CaseMenu
                    selectedCaseId={selectedCaseId}
                    setSelectedCaseId={setSelectedCaseId}
                    setNewCaseCreated={setNewCaseCreated}
                    newCaseCreated={newCaseCreated}
                    setSelectedCaseData={setSelectedCaseData}
                  />
                </Col>
                <Col flex="none">
                  <Row gutter={[0, 20]}>
                    {selectedCaseId && (
                      <>
                        {isConsentManageAllowed && (
                          <Button
                            type="primary"
                            onClick={() => {
                              setDocmanConsentModalVisible(true);
                            }}
                            style={{ marginRight: '20px' }}
                            data-testid="manage-consent-button"
                          >
                            {t('Manage Consent')}
                          </Button>
                        )}
                        <SelfDischarge
                          canDischarge={!isCaseDischarged}
                          caseId={Number(selectedCaseId)}
                          caseCondition={selectedCaseData?.condition}
                        />
                        <Button
                          type="primary"
                          disabled={isCaseDischarged}
                          onClick={() => {
                            setUploadModalVisible(true);
                          }}
                          data-testid="upload-button"
                        >
                          {t('Upload document')}
                        </Button>
                      </>
                    )}
                  </Row>
                </Col>
                {isCaseDischarged && (
                  <div>
                    {t('This case has been ')}
                    <b>{t('discharged')}</b>
                    {t('. Please complete any remaining actions below, as no further actions will be pro')}
                  </div>
                )}
              </Row>
              <PatientPageSubHeading style={{ margin: '20px 0px' }}>{t('Your documents')}</PatientPageSubHeading>
              <LetterDocuments
                pageSize={5}
                caseId={Number(selectedCaseId)}
                appId={Number(encryptedApptId)}
                refresh={documentsRefresh}
              />

              <GetDocmanConsentForm
                key={Number(docmanConsentModalVisible)}
                isOpen={docmanConsentModalVisible}
                setVisibility={setDocmanConsentModalVisible}
                caseName={selectedCaseData?.cat_desc}
                caseId={Number(selectedCaseData?.case_id)}
                surveyjsId={String(surveyWithCaseIdDetails?.survey_id)}
                userGuid={user?.user_guid}
                onCancel={() => {
                  setDocmanConsentModalVisible(false);
                }}
              />

              <PatientPageSubHeading style={{ margin: '32px 0px 20px 0px' }}>
                {t('Your question sets')}
              </PatientPageSubHeading>
              <FlexibleQuestions
                selfReferalID={newCaseCreated}
                pageSize={5}
                caseId={selectedCaseId as string}
                setSurveyInformation={setSurveyInformation}
              />
              <UploadDocumentModal
                visible={uploadModalVisible}
                setVisible={setUploadModalVisible}
                caseId={selectedCaseId as string}
                caseCondition={selectedCaseData?.condition}
                patientId={user.user_guid}
                afterDocumentUpload={() => setDocumentsRefresh((refresh) => refresh + 1)}
                isPatientDocsTabUpload
              />
            </>
          )}
        </main>
      </section>
    </div>
  );
};

const WrappedPatientDocuments = () => {
  const { t } = useTranslation('common');
  const initialised = useInitialisedStore();

  if (!initialised) {
    return <p>{t('Initialising ...')}</p>;
  }

  return <Documents />;
};


export const Route = createFileRoute('/patients/documents')({ component: WrappedPatientDocuments });
